import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-number-input';
import { Button, Modal, Select } from 'selfly-ui-react';
import { Label } from 'reactstrap';

import { withRouter, useHistory } from 'react-router-dom';

import { connect } from 'react-redux';
import { translate } from 'react-i18next';

import {
    fetchOrganisationSettingsLocationHints,
    fetchOrganisationSettings,
    updateOrganizationSettings,
    uploadOrganizationLogo,
    revertOrganizationLogoImageState,
    removeOrganizationLogoImage,
} from '../../../actions/organizations.js';
import { fetchLocations } from '../../../actions/cabinets.js';

import { ExtendedCountryList } from './countryList';

import Switch from 'react-switch';

import { ToastNotification, showNotification } from '../../toastNotification/toastNotification';

import ImageUpload from '../../imageUpload/imageUpload';

const ConsumerApp = props => {
    const {
        t,
        cabinets,
        organizationSettings,
        organizationSettingsLocationHints,
        uploadOrganizationLogo,
        removeOrganizationLogoImage,
        revertOrganizationLogoImageState,
    } = props;

    const isLocationRestricted = props.user.isLocationRestricted;

    const DEFAULT_LOCATION_VALUE = 'null';
    const DEFAULT_LOCATION_OPTION = {
        label: `-- ${t('default')} --`,
        value: DEFAULT_LOCATION_VALUE,
    };

    // locations have not been fetched
    if (!cabinets.locationsFetched && !cabinets.locationsIsFetching && !cabinets.locationsIsError) {
        props.fetchLocations();
    }

    if (
        !organizationSettingsLocationHints.locationHintsFetched &&
        !organizationSettingsLocationHints.locationHintsIsFetching &&
        !organizationSettingsLocationHints.locationHintsIsError
    ) {
        props.fetchOrganisationSettingsLocationHints();
    }

    const [newOrgSettings, setNewOrgSettings] = useState({
        enableReceipt: organizationSettings && organizationSettings.enableReceipt ? true : false,
        enableFeedback: organizationSettings && organizationSettings.enableFeedback ? true : false,
        enableSelflyStoreTrademark: organizationSettings && organizationSettings.enableSelflyStoreTrademark ? true : false,
    });
    const [cropping, toggleCropping] = useState(false);
    const [imageEditMode, setImageEditMode] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(DEFAULT_LOCATION_VALUE);
    const [locationSettings, setLocationSettings] = useState({});
    const [locationOptions, setLocationOptions] = useState([DEFAULT_LOCATION_OPTION]);
    const [selectedLocationOption, setSelectedLocationOption] = useState(DEFAULT_LOCATION_OPTION);
    const history = useHistory();

    const checkLocationHasCustomSettings = locationId => {
        return (
            organizationSettingsLocationHints &&
            organizationSettingsLocationHints.locationHints &&
            organizationSettingsLocationHints.locationHints[locationId] &&
            organizationSettingsLocationHints.locationHints[locationId].custom
        );
    };
    const checkLocationAncestorHasCustomSettings = locationId => {
        return (
            organizationSettingsLocationHints &&
            organizationSettingsLocationHints.locationHints &&
            organizationSettingsLocationHints.locationHints[locationId] &&
            organizationSettingsLocationHints.locationHints[locationId].customAncestor
        );
    };

    useEffect(() => {
        const options = [
            DEFAULT_LOCATION_OPTION,
            ...cabinets.locations.map(location => ({
                label: location.name,
                value: location.id,
            })),
        ];
        setLocationOptions(options);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cabinets.locations]);

    useEffect(() => {
        setSelectedLocationOption(locationOptions.find(opt => opt.value === selectedLocation) ?? DEFAULT_LOCATION_OPTION);

        const locationId = selectedLocation === DEFAULT_LOCATION_VALUE ? null : selectedLocation;
        if (locationSettings[locationId]) {
            setNewOrgSettings(locationSettings[locationId]);
        } else {
            props.fetchOrganisationSettings(locationId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocation]);

    useEffect(() => {
        const locationId = selectedLocation === DEFAULT_LOCATION_VALUE ? null : selectedLocation;
        if (organizationSettings) {
            const s = { ...props.organizationSettings };
            const ls = { ...locationSettings };
            ls[locationId] = s;
            setLocationSettings(ls);
            setNewOrgSettings(s);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationSettings]);

    const updateConsumerAppSettings = (key, value) => {
        const settings = { ...newOrgSettings };
        settings[key] = value;
        setNewOrgSettings(settings);
    };

    const closeForm = () => {
        history.push('/');
    };

    const saveOrganizationSettings = () => {
        const locationId = selectedLocation === DEFAULT_LOCATION_VALUE ? null : selectedLocation;

        // Only include fields that are part of the form
        const allowedFields = [
            'enableFeedback', 'enableReceipt', 'enableSelflyStoreTrademark',
            'organisationName', 'businessId',
            'country', 'city', 'street', 'postalCode',
            'phoneNumber', 'email', 'website',
            'organizationLogoUrl',
        ];
        const settings = {};
        allowedFields.forEach(key => {
            if (key in newOrgSettings) {
                settings[key] = newOrgSettings[key] === '' ? null : newOrgSettings[key];
            }
        });

        let missingMandatoryProperties = false;
        // TODO: Change API validation logic so that the following mandatory parameter check is skipped if receipt not enabled!
        // eslint-disable-next-line no-constant-condition
        if (true) {
            //if (settings.enableReceipt) {
            const mandatoryProperties = ['businessId', 'country', 'organisationName', 'street'];
            mandatoryProperties.forEach(property => {
                const dataKeys = Object.keys(settings);
                if (!dataKeys.includes(property)) missingMandatoryProperties = true;
                else if (!settings[property]) missingMandatoryProperties = true;
            });

            if (missingMandatoryProperties) {
                showNotification(t('consumerApp:organizationSettingsMissingFieldsTitle'), t('consumerApp:organizationSettingsMissingFields'), 'error');
                return false;
            }
        }

        setLocationSettings({});
        if (!missingMandatoryProperties) {
            props.updateOrganizationSettings(settings, props.user.group.Id, locationId).then(res => {
                if (res && res.error) {
                    showNotification(t('main:error'), t('main:unexpectedError'), 'error');
                } else {
                    showNotification(t('consumerApp:organizationSettingsUpdatedTitle'), t('consumerApp:organizationSettingsUpdatedContent'), 'success');
                    props.fetchOrganisationSettingsLocationHints();
                }
            });
        }
    };

    const setSelectedOrgLogo = () => {
        setNewOrgSettings({ ...newOrgSettings, organizationLogoUrl: organizationSettings.organizationLogoUrl });
        setImageEditMode(false);
    };
    const editLogo = () => {
        removeOrganizationLogoImage();
        setImageEditMode(true);
    };

    const toggleImgCropping = () => {
        toggleCropping(!cropping);
    };

    const cancelImageEditing = () => {
        setImageEditMode(false);
        revertOrganizationLogoImageState(organizationSettings?.organizationLogoUrl);
    };
    const LogoUpload = () => {
        if (imageEditMode) {
            return (
                <Modal title={t('main:logoEditor')} active={imageEditMode} onClose={() => setImageEditMode(false)}>
                    <div className="logoEditContainer d-flex mb-2 justify-content-between">
                        <div className="">
                            <div className="scp-settings__inputs-item">
                                <div className="logoEditContainer d-flex mb-2 justify-content-between">
                                    <ImageUpload
                                        className="se-input-container"
                                        targetWidth={parseInt(process.env.REACT_APP_COMPANY_LOGO_IMAGE_WIDTH) || 100}
                                        targetHeight={parseInt(process.env.REACT_APP_COMPANY_LOGO_IMAGE_HEIGHT) || 100}
                                        imageUrl={organizationSettings?.organizationLogoUrl || ''}
                                        storing={organizationSettings?.organizationLogoStoring || false}
                                        uploadImage={uploadOrganizationLogo}
                                        removeImage={removeOrganizationLogoImage}
                                        toggleCropping={toggleImgCropping}
                                        t={props.t}
                                        paddedAspectRatio={true}
                                    />
                                    <div className="d-flex justify-content-end">
                                        {organizationSettings.organizationLogoUrl && organizationSettings.organizationLogoUrl !== '' ? (
                                            <Button onClick={() => setSelectedOrgLogo()} className="mr-2">
                                                {t('use')}
                                            </Button>
                                        ) : null}
                                        <Button onClick={() => cancelImageEditing()}>{t('cancel')}</Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal>
            );
        }

        return (
            <div className="mt-3 w-100 d-flex align-items-center mb-2">
                <div className=" d-flex scp-settings__inputs ">
                    {organizationSettings?.organizationLogoUrl ? (
                        <img src={organizationSettings.organizationLogoUrl} className="logoImage" alt="logo" />
                    ) : (
                        <div className="ml-3">{t('noLogo')}</div>
                    )}
                </div>
                <div className="d-flex">
                    <Button className="se-btn se-btn--sm se-btn--secondary ml-3" icon="edit" onClick={() => editLogo()} />
                    {organizationSettings?.organizationLogoUrl && organizationSettings.organizationLogoUrl !== '' && (
                        <Button
                            className="se-btn se-btn--sm se-btn--secondary ml-3"
                            icon="delete"
                            onClick={() => {
                                organizationSettings.organizationLogoUrl = null;
                                setSelectedOrgLogo();
                            }}
                        />
                    )}
                </div>
            </div>
        );
    };
    return (
        <>
            <section className="scp-settings">
                <ToastNotification />

                {/** settings page breadcrumbs */}
                <div className="row">
                    <div className="col-md-6 scp-settings__breadcrumb">
                        <svg>
                            <use xlinkHref="#icon-params"></use>
                        </svg>
                        <span>{t('settings')}</span>
                        <div className="scp-settings__breadcrumb-direction">
                            <svg>
                                <use xlinkHref="#icon-arrow-down"></use>
                            </svg>
                        </div>
                        <span>MySelfly</span>
                    </div>

                    <div className="col-md-6">
                        <div style={{ marginRight: '25%' }}>
                            <div className="row">
                                <div className="col-md-6">
                                    <Label for="location">{t('locations')}</Label>
                                </div>
                                <div className="col-md-6"></div>
                            </div>
                            <div className="row">
                                <Select
                                    size="sm"
                                    onChange={({ value }) => {
                                        setSelectedLocation(value);
                                    }}
                                    isSearchable={false}
                                    value={selectedLocationOption}
                                    options={locationOptions.map(opt => ({
                                        label: (
                                            <span>
                                                <span>{opt.label}</span>
                                                {checkLocationHasCustomSettings(opt.value) && <span style={{ float: 'right' }}>*</span>}
                                                {checkLocationAncestorHasCustomSettings(opt.value) && <span style={{ float: 'right' }}>{'**'}</span>}
                                            </span>
                                        ),
                                        value: opt.value,
                                    }))}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <form className="scp-settings__form">
                    <div className="scp-settings__inputs scp-settings__inputs--margin">
                        <div className="scp-settings__inputs-switch">
                            <Switch
                                className="react-switch"
                                uncheckedIcon={false}
                                checked={newOrgSettings ? newOrgSettings.enableFeedback : false}
                                onChange={checked => updateConsumerAppSettings('enableFeedback', checked)}
                            />
                            <p className="mb-0">{t('consumerApp:enableConsumerFeedback')}</p>
                        </div>
                    </div>

                    <div className="scp-settings__inputs scp-settings__inputs--margin">
                        <div className="scp-settings__inputs-switch">
                            <Switch
                                className="react-switch"
                                uncheckedIcon={false}
                                checked={newOrgSettings ? newOrgSettings.enableReceipt : false}
                                onChange={checked => updateConsumerAppSettings('enableReceipt', checked)}
                            />
                            <p className="mb-0">{t('consumerApp:enablePurchaseReceipts')}</p>
                        </div>
                    </div>

                    <div className="scp-settings__inputs  scp-settings__inputs--margin">
                        <div className="scp-settings__inputs-switch">
                            <Switch
                                className="react-switch"
                                uncheckedIcon={false}
                                disabled={!newOrgSettings?.enableReceipt}
                                checked={newOrgSettings ? newOrgSettings.enableSelflyStoreTrademark : false}
                                onChange={checked => updateConsumerAppSettings('enableSelflyStoreTrademark', checked)}
                            />
                            <p className="mb-0">{t('consumerApp:enableSelflyTrademarkForReceipts')}</p>
                        </div>
                        <p className="scp-settings__inputs-info">{t('consumerApp:enableSelflyTrademarkForReceiptsHelpText')}</p>
                    </div>

                    <div className="scp-settings__note">
                        {t('consumerApp:organizationFormHelpText')} {t('consumerApp:organizationFormValidationHelpText')}
                    </div>

                    {/**Name of Org */}
                    <div className="scp-settings__group">
                        <div className="scp-settings__title">
                            <svg>
                                <use xlinkHref="#icon-office"></use>
                            </svg>
                            <p className="mb-0">{t('consumerApp:organization')}</p>
                        </div>
                        <div className="scp-settings__inputs">
                            <div className="scp-settings__inputs-item">
                                <input
                                    type="text"
                                    value={newOrgSettings?.organisationName ?? ''}
                                    placeholder={t('consumerApp:organizationName')}
                                    onChange={e => updateConsumerAppSettings('organisationName', e.target.value)}
                                />
                                <input
                                    type="text"
                                    value={newOrgSettings?.businessId ?? ''}
                                    placeholder={t('consumerApp:businessId')}
                                    onChange={e => updateConsumerAppSettings('businessId', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                    {/**Address of Org */}
                    <div className="scp-settings__group">
                        <div className="scp-settings__title">
                            <svg>
                                <use xlinkHref="#icon-location2"></use>
                            </svg>
                            <p className="mb-0">{t('consumerApp:addressHeader')}</p>
                        </div>
                        <div className="scp-settings__inputs country-selector-dropdown">
                            <div className="scp-settings__inputs-item">
                                <Select
                                    size="sm"
                                    onChange={({ value }) => {
                                        updateConsumerAppSettings('country', value);
                                    }}
                                    value={{ label: newOrgSettings?.country, value: newOrgSettings?.country }}
                                    options={ExtendedCountryList.sort((a, b) => a.name.localeCompare(b.name)).map(country => {
                                        return {
                                            label: country.name,
                                            value: country.name,
                                            className: country.name === newOrgSettings?.country ? 'language-dropdown-item-active' : null,
                                        };
                                    })}
                                />

                                <input
                                    type="text"
                                    value={newOrgSettings?.city ?? ''}
                                    placeholder={t('consumerApp:city')}
                                    onChange={e => updateConsumerAppSettings('city', e.target.value)}
                                />
                                <input
                                    type="text"
                                    value={newOrgSettings?.street ?? ''}
                                    placeholder={t('consumerApp:street')}
                                    onChange={e => updateConsumerAppSettings('street', e.target.value)}
                                />
                                <input
                                    type="text"
                                    value={newOrgSettings?.postalCode ?? ''}
                                    placeholder={t('consumerApp:postalCode')}
                                    onChange={e => updateConsumerAppSettings('postalCode', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                    {/**CONTACT details */}
                    <div className="scp-settings__group">
                        <div className="scp-settings__title">
                            <svg>
                                <use xlinkHref="#icon-contacts"></use>
                            </svg>
                            <p className="mb-0">{t('consumerApp:contactDetails')}</p>
                        </div>
                        <div className="scp-settings__inputs">
                            <div className="scp-settings__inputs-item">
                                <div className="scp-settings__inputs-phone">
                                    <PhoneInput
                                        international
                                        countryCallingCodeEditable={false}
                                        placeholder="Enter phone number"
                                        value={newOrgSettings?.phoneNumber ?? ''}
                                        defaultCountry={'FI'}
                                        withCountryCallingCode
                                        onChange={value => updateConsumerAppSettings('phoneNumber', value)}
                                    />
                                </div>

                                <input
                                    type="text"
                                    value={newOrgSettings?.email ?? ''}
                                    placeholder={t('consumerApp:email')}
                                    onChange={e => updateConsumerAppSettings('email', e.target.value)}
                                />
                                <input
                                    type="text"
                                    value={newOrgSettings?.website ?? ''}
                                    placeholder={t('consumerApp:website')}
                                    onChange={e => updateConsumerAppSettings('website', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="scp-settings__group">
                        <div className="scp-settings__title">
                            <svg>
                                <use xlinkHref="#icon-contacts"></use>
                            </svg>
                            <p className="mb-0">{t('consumerApp:organizationLogo')}</p>
                        </div>
                        {LogoUpload()}
                    </div>
                    <div className="scp-settings__group">
                        <Button className="cancel" onClick={() => closeForm()}>
                            {t('cancel')}
                        </Button>
                        <Button
                            className="save"
                            onClick={() => saveOrganizationSettings()}
                            disabled={isLocationRestricted && selectedLocation === DEFAULT_LOCATION_VALUE}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </form>
            </section>
        </>
    );
};
export default withRouter(
    connect(
        state => ({
            user: state.user,
            organizationSettings: state.organizationsSettings.organizationSettings,
            organizationSettingsLocationHints: {
                locationHintsFetched: state.organizationsSettings.locationHintsFetched,
                locationHintsIsFetching: state.organizationsSettings.locationHintsIsFetching,
                locationHintsIsError: state.organizationsSettings.locationHintsIsError,
                locationHints: state.organizationsSettings.locationHints,
            },
            cabinets: state.cabinets,
        }),
        {
            fetchOrganisationSettingsLocationHints,
            fetchLocations,
            fetchOrganisationSettings,
            updateOrganizationSettings,
            removeOrganizationLogoImage,
            uploadOrganizationLogo,
            revertOrganizationLogoImageState,
        }
    )(translate('main')(ConsumerApp))
);
